import React, { useState, useMemo, useEffect, useRef } from "react";
import { withTranslation } from "react-i18next";
import moment from "moment";
import constants from "../../../../../../../constants";
import {
  cacheSideBarActive,
  getCacheLocalstorage,
  setCacheLocalstorage,
} from "utils";
import {
  getOfficeNavigationContext,
  isInOfficeNavigationContext,
} from "utils/officeNavigationContext";
import { useAccountOwnerCommon } from "lib/Context/AccountOwner";
import {
  usePolicyCategory,
  useComplianceAnalytics,
} from "repositories/office-policy-management-repository";
import StickySidebar from "accountOwner/pages/Scheduler/components/StickySidebar";
import {
  Accordion,
  AccordionItem,
  AccordionItemButton,
  AccordionItemHeading,
  AccordionItemPanel,
} from "react-accessible-accordion";
import OfficeFilterByAllActive from "accountOwner/pages/OfficePolicyManagement/Filters/OfficeFilterByAllActive";
import CategoryFilter from "accountOwner/pages/OfficePolicyManagement/Filters/CategoryFilter";
import DateRangeFilter from "accountOwner/pages/OfficePolicyManagement/Filters/DateRangeFilter";
import styles from "accountOwner/pages/Notes/Notes.module.scss";
import complianceStyles from "accountOwner/pages/OfficePolicyManagement/OfficePolicyManagement.module.scss";
import { Spinner } from "reactstrap";
import NotAccessible from "accountOwner/pages/ManagerDashboard/components/NotAccessible";
import AnalyticalChart from "accountOwner/pages/OfficePolicyManagement/components/AnalyticalChart";
const defaultDateRange = () => ({
  fromDate: null,
  toDate: null,
});

/**
 * Filters for Compliance Analytics: Date Range, Offices, Category, Status (no Staff filter).
 */
const ComplianceAnalyticsFilters = ({
  t,
  isSidebarActive,
  handleSidebarToggle,
  resetFilter,
  officeFilter,
  setOfficeFilter,
  handleApplyFilters,
  categoryFilter,
  setCategoryFilter,
  categoryList,
  dateRange,
  setDateRange,
  isFilterApply,
  accountOwnerId,
}) => {
  const handleApplyFilter = () => {
    handleApplyFilters();
    const mediaQuery = window.matchMedia("(max-width: 767px)");
    if (mediaQuery.matches) handleSidebarToggle();
  };

  const handleResetFilter = () => {
    resetFilter();
    const mediaQuery = window.matchMedia("(max-width: 767px)");
    if (mediaQuery.matches) handleSidebarToggle();
  };

  const handleClose = () => {
    const mediaQuery = window.matchMedia("(max-width: 767px)");
    if (mediaQuery.matches) handleSidebarToggle();
  };

  return (
    <StickySidebar
      isSidebarActive={isSidebarActive}
      handleSidebarToggle={handleSidebarToggle}
      resetFilter={handleResetFilter}
    >
      <span className={styles.notes_filter}>
        {t("taskManagement.filters")}
        {isFilterApply && <span className={styles.red_dot}></span>}
      </span>
      <Accordion
        preExpanded={["date", "a", "cat"]}
        className="filter-accordion compliance-dashboard-accordion"
        allowZeroExpanded
      >
        <AccordionItem uuid="date">
          <AccordionItemHeading>
            <AccordionItemButton>
              {t("accountOwner.dateRange")}
            </AccordionItemButton>
          </AccordionItemHeading>
          <AccordionItemPanel>
            <DateRangeFilter
              dateRange={dateRange}
              setDateRange={setDateRange}
              t={t}
            />
          </AccordionItemPanel>
        </AccordionItem>
        <AccordionItem uuid="a" className="border-0 m-0 p-0">
          <AccordionItemHeading>
            <AccordionItemButton>
              {t("taskManagement.offices")}
            </AccordionItemButton>
          </AccordionItemHeading>
          <AccordionItemPanel>
            <OfficeFilterByAllActive
              selectedOffice={officeFilter}
              setOfficeFilter={setOfficeFilter}
              ownerId={accountOwnerId}
              t={t}
            />
          </AccordionItemPanel>
        </AccordionItem>
        <AccordionItem uuid="cat">
          <AccordionItemHeading>
            <AccordionItemButton>
              {t("officePolicyManagement.category")}
            </AccordionItemButton>
          </AccordionItemHeading>
          <AccordionItemPanel>
            <CategoryFilter
              categoryList={categoryList}
              selectedCategory={categoryFilter}
              setCategoryFilter={setCategoryFilter}
              t={t}
            />
          </AccordionItemPanel>
        </AccordionItem>
      </Accordion>
      <div className="filter-btn-box filter-btn-box--sticky-footer compliance-dashboard-filter-btns">
        <button
          className="button button-round button-shadow mr-3 compliance-apply-btn"
          title="apply"
          onClick={handleApplyFilter}
        >
          {t("taskManagement.apply")}
        </button>
        <button
          className="button button-round button-border button-dark reset-btn compliance-reset-btn"
          title="reset"
          onClick={handleResetFilter}
        >
          {t("taskManagement.reset")}
        </button>
        <button
          className="button button-round button-border button-dark cancel-btn compliance-cancel-btn"
          title="close"
          onClick={handleClose}
        >
          {t("taskManagement.close")}
        </button>
      </div>
    </StickySidebar>
  );
};

const ComplianceAnalytics = ({ t }) => {
  const {
    effectiveOwnerId: accountOwnerId,
    isStaff,
    dataAccessValues,
    isOwner,
  } = useAccountOwnerCommon();
  const canAccessThisPage = isOwner || (isStaff && dataAccessValues.isAdmin);
  const cannotAccessThisPage = !canAccessThisPage;

  const [isSidebarActive, setSidebarActive] = useState(
    cacheSideBarActive(
      constants.sidebar?.cache?.complianceAnalyticsIsSidebarActive,
    ),
  );

  // Load applied filters from cache
  const loadAppliedFiltersFromCache = () => {
    try {
      const cached = getCacheLocalstorage(
        constants.localStorageKeys.complianceAnalytics.appliedFilters,
        true,
      );
      if (cached) {
        return {
          appliedOfficeFilters: cached.appliedOfficeFilters || [],
          appliedCategoryFilters: cached.appliedCategoryFilters || [],
          appliedDateRange: cached.appliedDateRange
            ? {
                fromDate: cached.appliedDateRange.fromDate
                  ? new Date(cached.appliedDateRange.fromDate)
                  : null,
                toDate: cached.appliedDateRange.toDate
                  ? new Date(cached.appliedDateRange.toDate)
                  : null,
              }
            : { fromDate: null, toDate: null },
        };
      }
    } catch (error) {
      console.error("Error loading applied filters from cache:", error);
    }
    return {
      appliedOfficeFilters: [],
      appliedCategoryFilters: [],
      appliedDateRange: { fromDate: null, toDate: null },
    };
  };
  const preservedRef = useRef(null);
  const cachedAppliedFilters = useMemo(() => loadAppliedFiltersFromCache(), []);

  const [officeFilter, setOfficeFilter] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState([]);
  const [dateRange, setDateRange] = useState(defaultDateRange());

  const [appliedOfficeFilters, setAppliedOfficeFilters] = useState([]);
  const [appliedCategoryFilters, setAppliedCategoryFilters] = useState([]);
  const [appliedDateRange, setAppliedDateRange] = useState(defaultDateRange());
  useEffect(() => {
    const officeContext = getOfficeNavigationContext();
    if (isInOfficeNavigationContext(officeContext) && officeContext?.officeId) {
      const officeId = Number(officeContext.officeId);
      setOfficeFilter([officeId]);
      setAppliedOfficeFilters([officeId]);
      return;
    }
    setOfficeFilter(cachedAppliedFilters.appliedOfficeFilters);
    setAppliedOfficeFilters(cachedAppliedFilters.appliedOfficeFilters);
    setCategoryFilter(cachedAppliedFilters.appliedCategoryFilters);
    setAppliedCategoryFilters(cachedAppliedFilters.appliedCategoryFilters);
    setDateRange(cachedAppliedFilters.appliedDateRange);
    setAppliedDateRange(cachedAppliedFilters.appliedDateRange);
  }, [cachedAppliedFilters]);
  const { data: categoryResponse } = usePolicyCategory(accountOwnerId, {
    enabled: !!accountOwnerId && canAccessThisPage,
  });

  const categoryList = useMemo(() => {
    return categoryResponse?.data || [];
  }, [categoryResponse]);

  // Prepare API params from applied filters
  const complianceAnalyticsParams = useMemo(() => {
    const params = {};
    if (appliedCategoryFilters?.length > 0) {
      params.CategoryIds = appliedCategoryFilters;
    }
    if (appliedOfficeFilters?.length > 0) {
      params.OfficeIds = appliedOfficeFilters;
    }
    if (appliedDateRange?.fromDate) {
      params.StartDate = moment(appliedDateRange.fromDate).format("YYYY-MM-DD");
    }
    if (appliedDateRange?.toDate) {
      params.EndDate = moment(appliedDateRange.toDate).format("YYYY-MM-DD");
    }
    return params;
  }, [appliedCategoryFilters, appliedOfficeFilters, appliedDateRange]);

  const { data: complianceAnalyticsData, isLoading: isLoadingAnalytics } =
    useComplianceAnalytics(accountOwnerId, complianceAnalyticsParams, {
      enabled: !!accountOwnerId,
    });

  const isFilterApply = useMemo(() => {
    return (
      appliedOfficeFilters?.length > 0 ||
      appliedCategoryFilters?.length > 0 ||
      (appliedDateRange?.fromDate && appliedDateRange?.toDate)
    );
  }, [appliedOfficeFilters, appliedCategoryFilters, appliedDateRange]);

  const handleSidebarToggle = () => {
    const next = !isSidebarActive;
    setSidebarActive(next);
    if (constants.sidebar?.cache?.complianceAnalyticsIsSidebarActive) {
      localStorage.setItem(
        constants.sidebar.cache.complianceAnalyticsIsSidebarActive,
        next,
      );
    }
  };

  const handleApplyFilters = () => {
    setAppliedOfficeFilters(officeFilter);
    setAppliedCategoryFilters(categoryFilter);
    setAppliedDateRange(dateRange);

    // Save applied filters to cache
    try {
      setCacheLocalstorage(
        constants.localStorageKeys.complianceAnalytics.appliedFilters,
        {
          appliedOfficeFilters: officeFilter,
          appliedCategoryFilters: categoryFilter,
          appliedDateRange: {
            fromDate: dateRange.fromDate
              ? moment(dateRange.fromDate).format("YYYY-MM-")
              : null,
            toDate: dateRange.toDate
              ? moment(dateRange.toDate).format("YYYY-MM-DD")
              : null,
          },
        },
        true,
      );
    } catch (error) {
      console.error("Error saving applied filters to cache:", error);
    }
  };

  const resetFilter = () => {
    const resetState = {
      officeFilter: [],
      categoryFilter: [],
      dateRange: defaultDateRange(),
      appliedOfficeFilters: [],
      appliedCategoryFilters: [],
      appliedDateRange: defaultDateRange(),
    };

    setOfficeFilter(resetState.officeFilter);
    setCategoryFilter(resetState.categoryFilter);
    setDateRange(resetState.dateRange);
    setAppliedOfficeFilters(resetState.appliedOfficeFilters);
    setAppliedCategoryFilters(resetState.appliedCategoryFilters);
    setAppliedDateRange(resetState.appliedDateRange);

    // Clear cache
    try {
      localStorage.removeItem(
        constants.localStorageKeys.complianceAnalytics.appliedFilters,
      );
    } catch (error) {
      console.error("Error clearing filter cache:", error);
    }
  };
  useEffect(() => {
    if (accountOwnerId) {
      if (preservedRef.current === null) {
        preservedRef.current = accountOwnerId;
      } else if (preservedRef.current !== accountOwnerId) {
        resetFilter();
        preservedRef.current = accountOwnerId;
      }
    }
  }, [accountOwnerId]);
  // Transform API data to chart format, or use default if no data
  const chartData = useMemo(() => {
    if (complianceAnalyticsData?.items?.length > 0) {
      // Map API response to chart data format
      // Adjust this mapping based on actual API response structure
      return complianceAnalyticsData?.items?.map((item) => ({
        name: item.statusName,
        value: item.count || 0,
        color: item.colorCode || "#3F78ED",
      }));
    }
    // Default/fallback data structure
    return [
      { name: "In Progress", value: 0, color: "#3F78ED" },
      { name: "Pending", value: 0, color: "#FCC539" },
      { name: "Accepted", value: 0, color: "#71D5B9" },
      { name: "Completed", value: 0, color: "#A9CF3D" },
      { name: "Overdue", value: 0, color: "#EE4F4F" },
      { name: "Not Assigned", value: 0, color: "#B5BAC1" },
    ];
  }, [complianceAnalyticsData?.items]);
  const valuesCount = useMemo(() => {
    return (
      complianceAnalyticsData?.items?.reduce(
        (acc, item) => acc + item.count,
        0,
      ) || 0
    );
  }, [complianceAnalyticsData?.items]);
  return (
    <div className="pt-0 position-relative">
      <div className={complianceStyles.wrapper_card}>
        <div className="scheduler-tabs-main-wrapper">
          <ComplianceAnalyticsFilters
            t={t}
            isSidebarActive={isSidebarActive}
            handleSidebarToggle={handleSidebarToggle}
            resetFilter={resetFilter}
            officeFilter={officeFilter}
            setOfficeFilter={setOfficeFilter}
            handleApplyFilters={handleApplyFilters}
            categoryFilter={categoryFilter}
            setCategoryFilter={setCategoryFilter}
            categoryList={categoryList}
            dateRange={dateRange}
            setDateRange={setDateRange}
            isFilterApply={isFilterApply}
            accountOwnerId={accountOwnerId}
          />

          <div className={complianceStyles.compliance_table_wrapper}>
            {cannotAccessThisPage ? (
              <NotAccessible Message={t("dashBoard.notAccessible")} />
            ) : isLoadingAnalytics ? (
              <div style={{ padding: "1rem", textAlign: "center" }}>
                <Spinner
                  animation="border"
                  size="sm"
                  className="loader-spinner"
                />
              </div>
            ) : valuesCount > 0 ? (
              <AnalyticalChart data={chartData} height={500} />
            ) : (
              <div className="text-center p-5">
                No data available for your selection
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default withTranslation()(ComplianceAnalytics);
